// src/components/main/ErrorBoundary.jsx
import React from "react";

/**
 * ErrorBoundary
 * - Красивый оверлей на всю страницу
 * - Кнопки: «Попробовать снова», «Обновить», «Назад»
 * - Детали ошибки (stack + componentStack) с копированием
 *
 * Props:
 *  - onReset?: () => void                // своя логика сброса, если нужна
 *  - showDetails?: boolean               // форс-показ деталей (по умолчанию: не прод)
 *  - supportLink?: string                // ссылка на поддержку/чат
 *  - appName?: string                    // имя приложения
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: props.showDetails ?? process.env.NODE_ENV !== "production",
      copied: false,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    // тут можно отправить лог на сервер
    console.error("ErrorBoundary caught an error", error, errorInfo);
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, errorInfo);
      } catch {}
    }
  }

  handleReset = () => {
    if (typeof this.props.onReset === "function") {
      try {
        this.props.onReset();
      } catch {}
    }
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleBack = () => {
    if (window.history.length > 1) window.history.back();
  };

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  copyDetails = async () => {
    const { error, errorInfo } = this.state;
    const text = [
      `[${this.props.appName || "App"}] Ошибка:`,
      error?.name ? `${error.name}: ${error.message || ""}` : `${error?.message || "unknown error"}`,
      "",
      error?.stack ? `Stack:\n${error.stack}` : "",
      errorInfo?.componentStack ? `\nComponent stack:\n${errorInfo.componentStack}` : "",
      "",
      `URL: ${window.location.href}`,
      `UserAgent: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { showDetails, copied, error, errorInfo } = this.state;
    const { supportLink, appName = "Приложение" } = this.props;

    return (
      <div
        className="fixed inset-0 z-[100] grid place-items-center p-4
                   bg-slate-950/80 backdrop-blur-xl
                   [background-image:radial-gradient(900px_400px_at_50%_-10%,rgba(139,92,246,0.25),transparent)]"
        role="alert"
        aria-live="assertive"
      >
        {/* Карточка */}
        <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-900/70 shadow-2xl">
          {/* Заголовок */}
          <div className="flex items-center gap-3 px-5 pt-5">
            <div className="relative h-10 w-10">
              {/* значок-предупреждение */}
              <svg
                viewBox="0 0 24 24"
                className="absolute inset-0 h-10 w-10 text-violet-400 drop-shadow-[0_0_12px_rgba(139,92,246,0.6)]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-white">
                Упс! Что-то пошло не так
              </h1>
              <p className="text-sm text-white/70">
                {appName} столкнулось с ошибкой. Вы можете попробовать снова или обновить
                страницу. Если проблема повторяется — отправьте отчёт.
              </p>
            </div>
          </div>

          {/* Действия */}
          <div className="px-5 pt-4 flex flex-wrap gap-2">
            <button
              onClick={this.handleReset}
              className="rounded-lg px-3 py-2 bg-violet-600 text-white hover:bg-violet-700 transition"
            >
              Попробовать снова
            </button>
            <button
              onClick={this.handleReload}
              className="rounded-lg px-3 py-2 bg-white/10 text-white border border-white/15 hover:bg-white/20 transition"
            >
              Обновить страницу
            </button>
            <button
              onClick={this.handleBack}
              className="rounded-lg px-3 py-2 bg-white/10 text-white border border-white/15 hover:bg-white/20 transition"
            >
              Назад
            </button>

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={this.toggleDetails}
                className="rounded-lg px-3 py-2 text-white/90 hover:text-white hover:bg-white/5 border border-white/10 transition"
              >
                {showDetails ? "Скрыть детали" : "Показать детали"}
              </button>
              {supportLink && (
                <a
                  href={supportLink}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg px-3 py-2 text-white/90 hover:text-white hover:bg-white/5 border border-white/10 transition"
                >
                  Поддержка
                </a>
              )}
            </div>
          </div>

          {/* Детали ошибки */}
          {showDetails && (
            <div className="px-5 pt-3">
              <div className="rounded-xl bg-black/40 border border-white/10 p-3 overflow-auto max-h-64">
                <pre className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">
{(error?.name ? `${error.name}: ` : "") + (error?.message || "unknown error")}
{"\n\n"}
{error?.stack || ""}
{errorInfo?.componentStack ? `\n\nComponent stack:${errorInfo.componentStack}` : ""}
                </pre>
              </div>

              <div className="flex items-center justify-between py-4">
                <div className="text-xs text-white/60">
                  URL: {typeof window !== "undefined" ? window.location.href : ""}
                </div>
                <button
                  onClick={this.copyDetails}
                  className="rounded-lg px-3 py-1.5 text-sm bg-white/10 text-white border border-white/15 hover:bg-white/20 transition"
                >
                  {copied ? "Скопировано ✓" : "Скопировать детали"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
